import { useEffect, useRef, useCallback } from 'react';

export interface UseInactivityTimerOptions {
  /** Total inactivity timeout in ms (default: 30 minutes) */
  timeoutMs?: number;
  /** When to fire the warning callback, in ms before timeout (default: 5 minutes) */
  warningBeforeMs?: number;
  /** Only run when true */
  enabled?: boolean;
  /** Called when the full timeout elapses */
  onTimeout: () => void;
  /** Called when the warning threshold is reached. Receives ms remaining. */
  onWarning: (remainingMs: number) => void;
  /** Called when user becomes active again after warning was shown */
  onActive: () => void;
  /** Throttled user activity, including before the warning. */
  onActivity?: () => void;
}

const ACTIVITY_EVENTS = [
  'mousemove',
  'keydown',
  'click',
  'scroll',
  'touchstart',
] as const;

const THROTTLE_MS = 30_000; // only record activity once per 30s
const CHECK_INTERVAL_MS = 60_000; // check every 60s

export function useInactivityTimer({
  timeoutMs = 30 * 60 * 1000,
  warningBeforeMs = 5 * 60 * 1000,
  enabled = true,
  onTimeout,
  onWarning,
  onActive,
  onActivity,
}: UseInactivityTimerOptions): void {
  const lastActivityRef = useRef(Date.now());
  const lastThrottledRef = useRef(Date.now());
  const warningShownRef = useRef(false);
  const activityChannelRef = useRef<BroadcastChannel | null>(null);

  // Keep callbacks in refs so the effect doesn't re-run on every render
  const onTimeoutRef = useRef(onTimeout);
  const onWarningRef = useRef(onWarning);
  const onActiveRef = useRef(onActive);
  const onActivityRef = useRef(onActivity);
  onTimeoutRef.current = onTimeout;
  onWarningRef.current = onWarning;
  onActiveRef.current = onActive;
  onActivityRef.current = onActivity;

  const recordActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastThrottledRef.current < THROTTLE_MS) return;
    lastThrottledRef.current = now;
    lastActivityRef.current = now;
    onActivityRef.current?.();
    activityChannelRef.current?.postMessage(now);

    // If warning was shown and user became active, notify
    if (warningShownRef.current) {
      warningShownRef.current = false;
      onActiveRef.current();
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    // Reset state when enabled
    lastActivityRef.current = Date.now();
    lastThrottledRef.current = Date.now();
    warningShownRef.current = false;

    // Coordinate only activity timestamps across admin tabs; never share credentials.
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel('tripalta-admin-activity');
      activityChannelRef.current = channel;
      channel.onmessage = event => {
        if (
          typeof event.data !== 'number' ||
          event.data > Date.now() ||
          event.data <= lastActivityRef.current
        )
          return;
        lastActivityRef.current = event.data;
        if (warningShownRef.current) {
          warningShownRef.current = false;
          onActiveRef.current();
        }
      };
    }

    // Attach DOM listeners
    for (const event of ACTIVITY_EVENTS) {
      document.addEventListener(event, recordActivity, { passive: true });
    }

    // Periodic check
    const intervalId = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;

      if (elapsed >= timeoutMs) {
        onTimeoutRef.current();
        return;
      }

      const warningThreshold = timeoutMs - warningBeforeMs;
      if (elapsed >= warningThreshold && !warningShownRef.current) {
        warningShownRef.current = true;
        onWarningRef.current(timeoutMs - elapsed);
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      for (const event of ACTIVITY_EVENTS) {
        document.removeEventListener(event, recordActivity);
      }
      clearInterval(intervalId);
      activityChannelRef.current?.close();
      activityChannelRef.current = null;
    };
  }, [enabled, timeoutMs, warningBeforeMs, recordActivity]);
}
